import React, { useEffect, useMemo, useState } from 'react';
import { Search, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import EmptyState from '../EmptyState/EmptyState';

export interface FilterOption {
  value: string;
  label: string;
}

export interface Column<T> {
  key: keyof T & string;
  header: string;
  sortable?: boolean;
  filterable?: boolean;
  filterOptions?: FilterOption[];
  render?: (row: T) => React.ReactNode;
  align?: 'left' | 'right';
}

interface Props<T> {
  columns: Column<T>[];
  data: T[];
  rowKey: keyof T | ((row: T) => string | number);
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  /** @deprecated Use emptyTitle */
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  actions?: (row: T) => React.ReactNode;
  pageSize?: number;
  searchPlaceholder?: string;
}

type SortDir = 'asc' | 'desc' | null;

function useDebouncedValue<T>(value: T, delay: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setV(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return v;
}

function DataTable<T>({
  columns,
  data,
  rowKey,
  loading = false,
  emptyTitle,
  emptyDescription,
  emptyAction,
  emptyMessage,
  onRowClick,
  actions,
  pageSize = 25,
  searchPlaceholder = 'Search...',
}: Props<T>) {
  const [sortKey, setSortKey] = useState<(keyof T & string) | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput, 200);
  const [page, setPage] = useState(1);

  const keyFor = (row: T): string | number =>
    typeof rowKey === 'function' ? rowKey(row) : (row[rowKey] as unknown as string | number);

  const processed = useMemo(() => {
    let out = data.slice();

    for (const [col, val] of Object.entries(filters)) {
      if (!val) continue;
      out = out.filter((r) => String((r as any)[col]) === val);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      out = out.filter((r) =>
        columns.some((c) => {
          const v = (r as any)[c.key];
          return v != null && String(v).toLowerCase().includes(q);
        })
      );
    }

    if (sortKey && sortDir) {
      out.sort((a, b) => {
        const av = (a as any)[sortKey];
        const bv = (b as any)[sortKey];
        if (av === bv) return 0;
        const cmp = av > bv ? 1 : -1;
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }

    return out;
  }, [data, filters, search, sortKey, sortDir, columns]);

  const totalPages = Math.max(1, Math.ceil(processed.length / pageSize));
  const pageSafe = Math.min(page, totalPages);
  const startIdx = (pageSafe - 1) * pageSize;
  const visible = processed.slice(startIdx, pageSafe * pageSize);

  useEffect(() => { setPage(1); }, [search, filters, sortKey, sortDir]);

  const toggleSort = (key: keyof T & string) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
    } else if (sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortKey(null);
      setSortDir(null);
    }
  };

  const filterableCols = columns.filter((c) => c.filterable && c.filterOptions);
  const hasToolbar = true;

  const renderCell = (row: T, c: Column<T>) =>
    c.render ? c.render(row) : (row as any)[c.key] != null ? String((row as any)[c.key]) : '—';

  return (
    <div className="flex flex-col gap-4">
      {hasToolbar && (
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-72">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
              aria-hidden
            />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label="Search"
              className="h-11 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-base text-slate-900 placeholder:text-slate-400 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20"
            />
          </div>
          {filterableCols.map((c) => (
            <label key={c.key} className="flex items-center gap-2 text-sm text-slate-700">
              <span className="sr-only">{`Filter by ${c.header}`}</span>
              <select
                aria-label={`Filter by ${c.header}`}
                value={filters[c.key] ?? ''}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, [c.key]: e.target.value }))
                }
                className="h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-600/20"
              >
                <option value="">{`All ${c.header}`}</option>
                {c.filterOptions!.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          ))}
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-card md:block">
        <table className="min-w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50/80">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  className={`px-5 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 ${
                    c.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                >
                  {c.sortable ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(c.key)}
                      className="inline-flex items-center gap-1 hover:text-slate-900"
                    >
                      {c.header}
                      {sortKey === c.key && sortDir === 'asc' && (
                        <ArrowUp className="h-3 w-3" aria-hidden />
                      )}
                      {sortKey === c.key && sortDir === 'desc' && (
                        <ArrowDown className="h-3 w-3" aria-hidden />
                      )}
                      {sortKey !== c.key && (
                        <ArrowUpDown className="h-3 w-3 text-slate-400" aria-hidden />
                      )}
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              ))}
              {actions && (
                <th
                  scope="col"
                  className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600"
                >
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="px-5 py-10">
                  <LoadingSpinner centered label="Loading…" />
                </td>
              </tr>
            )}
            {!loading && visible.length === 0 && (
              <tr>
                <td colSpan={columns.length + (actions ? 1 : 0)} className="px-5 py-4">
                  <EmptyState
                    title={emptyTitle ?? emptyMessage ?? 'Nothing here yet'}
                    description={emptyDescription}
                    action={emptyAction}
                  />
                </td>
              </tr>
            )}
            {!loading && visible.map((row) => (
              <tr
                key={keyFor(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={onRowClick ? 'cursor-pointer transition-colors hover:bg-brand-50/50' : ''}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-5 py-3.5 text-base text-slate-800 ${
                      c.align === 'right' ? 'text-right tabular-nums' : ''
                    }`}
                  >
                    {renderCell(row, c)}
                  </td>
                ))}
                {actions && (
                  <td className="px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-2">
                      {actions(row)}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile stacked cards */}
      <div className="flex flex-col gap-2 md:hidden">
        {loading && (
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <LoadingSpinner centered label="Loading…" />
          </div>
        )}
        {!loading && visible.length === 0 && (
          <div className="rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
            <EmptyState
              title={emptyTitle ?? emptyMessage ?? 'Nothing here yet'}
              description={emptyDescription}
              action={emptyAction}
            />
          </div>
        )}
        {!loading && visible.map((row) => (
          <div
            key={keyFor(row)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={`rounded-xl border border-slate-200 bg-white p-4 shadow-sm ${
              onRowClick ? 'cursor-pointer active:bg-brand-50' : ''
            }`}
          >
            <dl className="flex flex-col gap-2">
              {columns.map((c) => (
                <div key={c.key} className="flex items-start justify-between gap-3">
                  <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {c.header}
                  </dt>
                  <dd className="text-sm text-slate-900 text-right">
                    {renderCell(row, c)}
                  </dd>
                </div>
              ))}
            </dl>
            {actions && (
              <div
                className="mt-3 flex items-center justify-end gap-2 border-t border-slate-100 pt-3"
                onClick={(e) => e.stopPropagation()}
              >
                {actions(row)}
              </div>
            )}
          </div>
        ))}
      </div>

      {processed.length > pageSize && (
        <div className="flex flex-col items-center justify-between gap-3 text-sm text-slate-600 sm:flex-row">
          <span>
            Showing <span className="font-semibold text-slate-900">{startIdx + 1}</span>
            –<span className="font-semibold text-slate-900">{Math.min(startIdx + pageSize, processed.length)}</span>
            {' '}of <span className="font-semibold text-slate-900">{processed.length}</span>
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={pageSafe <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="h-9 rounded-md border border-slate-300 bg-white px-3 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            <span className="text-slate-500">
              Page <span className="font-semibold text-slate-900">{pageSafe}</span> of{' '}
              <span className="font-semibold text-slate-900">{totalPages}</span>
            </span>
            <button
              type="button"
              disabled={pageSafe >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="h-9 rounded-md border border-slate-300 bg-white px-3 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataTable;
